import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Alert, Spinner, Row, Col, Card, Form, Badge } from 'react-bootstrap';
import { batchStore } from '../stores/BatchStore';
import { userStore } from '../stores/UserStore';
const BatchMembersDialog = observer(({ batchId, show, onHide, type }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const batch = batchStore.batches.find(b => b.id === batchId);
    const isCoachDialog = type === 'coaches';
    const currentMembers = isCoachDialog ? batch?.coaches || [] : batch?.players || [];
    // Get available users (coaches or players) from the academy
    const availableUsers = userStore.users.filter(user => {
        const hasRole = isCoachDialog ? user.roles.includes('coach') : user.roles.includes('player');
        const notInBatch = !currentMembers.includes(user.id);
        const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase());
        return hasRole && notInBatch && matchesSearch;
    });
    useEffect(() => {
        if (show) {
            setSelectedUsers([]);
            setSearchTerm('');
            setError(null);
        }
    }, [show]);
    const handleUserToggle = (userId) => {
        setSelectedUsers(prev => prev.includes(userId)
            ? prev.filter(id => id !== userId)
            : [...prev, userId]);
    };
    const handleSelectAll = () => {
        if (selectedUsers.length === availableUsers.length) {
            setSelectedUsers([]);
        }
        else {
            setSelectedUsers(availableUsers.map(user => user.id));
        }
    };
    const handleSubmit = async () => {
        if (selectedUsers.length === 0) {
            setError('Please select at least one user');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            if (isCoachDialog) {
                await batchStore.addCoachesToBatch(batchId, selectedUsers);
            }
            else {
                await batchStore.addPlayersToBatch(batchId, selectedUsers);
            }
            onHide();
        }
        catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to add users to batch');
        }
        finally {
            setLoading(false);
        }
    };
    const handleRemoveUser = async (userId) => {
        setLoading(true);
        setError(null);
        try {
            if (isCoachDialog) {
                await batchStore.removeCoachesFromBatch(batchId, [userId]);
            }
            else {
                await batchStore.removePlayersFromBatch(batchId, [userId]);
            }
        }
        catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to remove user from batch');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(Modal, { show: show, onHide: onHide, size: "lg", children: [_jsx(Modal.Header, { closeButton: true, children: _jsxs(Modal.Title, { children: ["Manage ", isCoachDialog ? 'Coaches' : 'Players', " for ", batch?.name] }) }), _jsxs(Modal.Body, { children: [error && (_jsx(Alert, { variant: "danger", className: "mb-3", children: error })), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Card, { children: [_jsx(Card.Header, { children: _jsxs("h6", { className: "mb-0", children: ["Current ", isCoachDialog ? 'Coaches' : 'Players'] }) }), _jsx(Card.Body, { style: { maxHeight: '300px', overflowY: 'auto' }, children: currentMembers.length === 0 ? (_jsxs("p", { className: "text-muted", children: ["No ", isCoachDialog ? 'coaches' : 'players', " assigned"] })) : (currentMembers.map(memberId => {
                                                const user = userStore.users.find(u => u.id === memberId);
                                                return user ? (_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2 p-2 border rounded", children: [_jsxs("div", { children: [_jsx("strong", { children: user.name }), user.email && _jsx("div", { className: "text-muted small", children: user.email })] }), _jsx(Button, { variant: "outline-danger", size: "sm", onClick: () => handleRemoveUser(memberId), disabled: loading, children: _jsx("i", { className: "bi bi-x" }) })] }, memberId)) : null;
                                            })) })] }) }), _jsx(Col, { md: 6, children: _jsxs(Card, { children: [_jsx(Card.Header, { children: _jsxs("h6", { className: "mb-0", children: ["Add ", isCoachDialog ? 'Coaches' : 'Players'] }) }), _jsxs(Card.Body, { children: [_jsx(Form.Group, { className: "mb-3", children: _jsx(Form.Control, { type: "text", placeholder: `Search ${isCoachDialog ? 'coaches' : 'players'}...`, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) }) }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsxs("small", { className: "text-muted", children: [availableUsers.length, " ", isCoachDialog ? 'coaches' : 'players', " available"] }), availableUsers.length > 0 && (_jsx(Button, { variant: "outline-primary", size: "sm", onClick: handleSelectAll, children: selectedUsers.length === availableUsers.length ? 'Deselect All' : 'Select All' }))] }), _jsx("div", { style: { maxHeight: '250px', overflowY: 'auto' }, children: availableUsers.length === 0 ? (_jsx("p", { className: "text-muted", children: searchTerm ? 'No matching users found' : `No available ${isCoachDialog ? 'coaches' : 'players'}` })) : (availableUsers.map(user => (_jsxs("div", { className: "d-flex align-items-center mb-2 p-2 border rounded", children: [_jsx(Form.Check, { type: "checkbox", checked: selectedUsers.includes(user.id), onChange: () => handleUserToggle(user.id), disabled: loading }), _jsxs("div", { className: "ms-2 flex-grow-1", children: [_jsx("strong", { children: user.name }), user.email && _jsx("div", { className: "text-muted small", children: user.email }), _jsx("div", { children: user.roles.map(role => (_jsx(Badge, { bg: "secondary", className: "me-1", children: role }, role))) })] })] }, user.id)))) })] })] }) })] })] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: onHide, disabled: loading, className: "btn-touch w-mobile-100", children: "Close" }), _jsxs(Button, { variant: "primary", onClick: handleSubmit, disabled: loading || selectedUsers.length === 0, className: "btn-touch w-mobile-100", children: [loading && (_jsx(Spinner, { animation: "border", size: "sm", className: "me-2" })), "Add Selected (", selectedUsers.length, ")"] })] })] }));
});
export default BatchMembersDialog;
